import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import { Formik, Form, Field } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react/lib/MessageBar';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { useLocation } from 'react-router-dom';
import { FontIcon, IconButton, PrimaryButton, DefaultButton } from 'office-ui-fabric-react';
import { TextField as FluentTextField } from 'office-ui-fabric-react/lib/TextField';
import { createDimensionList, editDimensionList } from '../../actions/dimensionList';
import Module from '../Module';
import ModuleContent from '../ModuleContent';
import StackGrid from '../StackGrid';
import SubModule from '../SubModule';
import SubModuleHeader from '../SubModuleHeader';
import SubModuleContent from '../SubModuleContent';
import Dropdown from '../Dropdown';
import TextField from '../TextField';
import Link from '../Link';
import { dimensionListTypes, dlSilos, dLTypeValues } from '../../constants';
import styles from './DimensionListForm.style';
import DimensionListItems from '../../components/DimensionListItems';
import TextButton from '../../components/TextButton';
import SavingIndicator from '../../components/SavingIndicator';
import DimensionSearchList from '../DimensionSearchList';
import { getUser } from '../../utils/auth';

const validFormFields = [
  'listId',
  'listType',
  'listName',
  'listDescription',
  'listItems',
  'contracts',
  'metadata',
  'reviewer',
];

const DimensionListForm = React.forwardRef(
  (
    {
      onSubmit,
      onSuccess,
      onFailure,
      reviewMode,
      dimensionList,
      handleCancel,
      handleSubmit,
      handleDiscard,
    },
    ref,
  ) => {
    const [currentListType, setCurrentListType] = useState('product');
    const validationSchema = Yup.object().shape({
      listType: Yup.string().required('Required').label('Name'),
      listName: Yup.string().max(255).required('Required'),
      listDescription: Yup.string(),
      listItems: Yup.array(),
      contracts: Yup.array(),
      metadata: Yup.object().shape({
        silo: currentListType === 'product' ? Yup.string().required('Required') : Yup.string(),
      }),
      reviewer: Yup.object().shape({
        reviewerId:
          dimensionList?.contracts?.length > 0 ? Yup.string().required('Required') : Yup.string(),
        reviewerJustification:
          dimensionList?.contracts?.length > 0 ? Yup.string().required('Required') : Yup.string(),
      }),
    });
    const [showSiloField, setShowSiloField] = useState(false);
    const dispatch = useDispatch();
    const { listFormData } = useLocation();
    const [initialValues, setinitialValues] = useState({
      listType: 'product',
      listName: '',
      listDescription: '',
      listItems: [],
      contracts: [],
      metadata: {
        silo: '',
      },
    });
    const user = getUser();
    const reviewers = useSelector(state =>
      Object.values(get(state, 'entities.users', {}))
        .filter(
          reviewer => user.profile.oid !== reviewer.oid && reviewer.displayName && reviewer.oid,
        )
        .map(reviewer => ({
          key: reviewer.oid,
          text: reviewer.displayName,
        })),
    );

    useEffect(() => {
      if (!isEmpty(dimensionList)) {
        if (dimensionList.contracts?.length > 0 && dimensionList.reviewer === undefined)
          // eslint-disable-next-line no-param-reassign
          dimensionList.reviewer = {
            reviewerJustification: '',
            reviewerId: dimensionList.reviewerId || '',
          };
        setinitialValues(pick(dimensionList, validFormFields));
      }
    }, [dispatch]);

    const submitHandler = async (submittedValues, { setErrors }) => {
      let response;
      const { listId, listType } = submittedValues;

      const listItemsValidFieldsOnly = submittedValues.listItems.map(item => {
        if (item.isList) return { id: item.id, isList: item.isList };
        if (dLTypeValues[listType].hierarchy) return { id: item.id, hierarchy: item.hierarchy };
        return {
          id: item.id,
        };
      });

      const formValues = {
        ...submittedValues,
        listItems: listItemsValidFieldsOnly,
        metadata: {
          silo: submittedValues.metadata.silo,
        },
      };

      response = listId
        ? await dispatch(editDimensionList(formValues))
        : await dispatch(createDimensionList(formValues));

      if (response.error && response.payload.message) {
        const error = new Error(response.payload.message);
        error.payload = response.payload.message;
        setErrors({ form: error.message });
        await onFailure(response);
        return response;
      }

      response = get(
        response,
        `payload.entities.dimensionLists.${get(response, 'payload.result')}`,
        {},
      );
      await onSuccess(response);

      return response;
    };

    return (
      <Formik
        innerRef={ref}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={onSubmit || submitHandler}
      >
        {({ errors, isSubmitting, values, setFieldValue, setValues, dirty }) => {
          const [enableListItemTextField, setEnableListItemTextField] = useState(null);
          const [newDimensionListItem, setNewDimensionListItem] = useState(null);
          const [listId, setListId] = useState(null);
          const [associatedContractDetails, setAssociatedContractDetails] = useState(null);
          const [listNameCharLimit, setListNameCharLimit] = useState('0/255 characters');
          const [disableListType, setDisableListType] = useState(false);
          const [isResubmitting, setIsResubmitting] = useState(false);

          const handleNewListItemSave = () => {
            if (newDimensionListItem) {
              const { listItems } = values;
              const newListItems = listItems.filter(i => i.id !== newDimensionListItem);
              newListItems.push({
                name: newDimensionListItem,
                id: newDimensionListItem,
                isList: false,
              });
              setFieldValue('listItems', newListItems);
            }
            setNewDimensionListItem(null);
            setEnableListItemTextField(false);
          };

          const submitBtnText = () => {
            if (associatedContractDetails) {
              return associatedContractDetails.length > 0 ? 'Submit for Approval' : 'Save';
            }
            return 'Save';
          };

          useEffect(() => {
            if (listFormData) {
              setValues(listFormData);
            }
          }, [initialValues]);

          useEffect(() => {
            if (values) {
              const listsWithSilo = ['product', 'offerId', 'resourceGuid'];
              const { listType } = values;
              if (listsWithSilo.includes(listType)) setShowSiloField(true);
              else setShowSiloField(false);
              setCurrentListType(values.listType);
              setListId(values.listId);
              setAssociatedContractDetails(dimensionList.associatedContractDetails);
              setIsResubmitting(dimensionList.status === 'Rejected');
              setListNameCharLimit(`${values.listName.length}/255 characters`);
              if (values.listItems.length > 0) setDisableListType(true);
              else setDisableListType(false);
            }
          }, [values]);

          const handleRemoveItem = item => {
            const { listItems } = values;
            const newListItems = listItems.filter(i => i.name !== item.name);
            setFieldValue('listItems', newListItems);
          };
          const handleRemoveAllLists = () => {
            const { listItems } = values;
            const newListItems = listItems.filter(item => !item.isList);
            setFieldValue('listItems', newListItems);
          };
          const handleRemoveAllItems = () => {
            const { listItems } = values;
            const newListItems = listItems.filter(item => item.isList);
            setFieldValue('listItems', newListItems);
          };

          const viewStagingColumn = {
            key: 'view',
            isResizable: false,
            name: 'View',
            styles: { root: { visibility: 'hidden' } },
            headerClassName: styles.iconColumnHeader,
            minWidth: 25,
            maxWidth: 25,
            columnActionsMode: 0,
            onRender: item => {
              return (
                <Link
                  to={`${item.id}/${currentListType}`}
                  target="_blank"
                  className={styles.itemText}
                >
                  View
                </Link>
              );
            },
          };

          const hierarchyColumn = {
            key: 'view',
            name: 'Hierarchy',
            isResizable: false,
            headerClassName: styles.iconColumnHeader,
            minWidth: 100,
            maxWidth: 200,
            columnActionsMode: 0,
            onRender: item => {
              return <span className={styles.itemText}>{item.hierarchy}</span>;
            },
          };

          const stagingColumns = [
            {
              key: 'icon',
              name: 'Lists',
              headerClassName: styles.iconColumnHeader,
              fieldName: 'icon',
              columnActionsMode: 0,
              minWidth: 8,
              maxWidth: 8,
              onRender: () => {
                return (
                  <div className={styles.iconColumn}>
                    <FontIcon
                      iconName="BulletedList"
                      ariaLabel="BulletedList"
                      className={styles.detailRowIcon}
                    />
                  </div>
                );
              },
            },
            {
              key: 'name',
              name: 'Item Name',
              styles: { root: { visibility: 'hidden' } },
              columnActionsMode: 0,
              fieldName: 'name',
              minWidth: 480,
              maxWidth: 810,
              isResizable: true,
              onRender: item => {
                return item.name;
              },
            },
            {
              key: 'remove',
              name: 'Remove All',
              arianame: 'remove',
              fieldName: 'remove',
              onColumnClick: handleRemoveAllLists,
              headerClassName: styles.iconColumnHeader,
              minWidth: 70,
              maxWidth: 70,
              className: styles.iconColumn,
              onRender: (item, index) => {
                const iconName = 'Cancel';
                return (
                  <IconButton
                    iconProps={{ iconName }}
                    ariaLabel="remove"
                    menuIconProps={{ iconName: '' }}
                    id={`listRemoveButton${index}`}
                    onClick={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleRemoveItem(item);
                    }}
                  />
                );
              },
            },
          ];

          const stagingColumnsItems = [
            {
              key: 'items',
              name: 'Items',
              headerClassName: styles.iconColumnHeader,
              minWidth: 580,
              maxWidth: 680,
              columnActionsMode: 0,
              onRender: item => {
                return <span className={styles.itemsTitle}>{item.name}</span>;
              },
            },
            {
              key: 'remove',
              name: 'Remove All',
              ariaLabel: 'remove',
              fieldName: 'remove',
              onColumnClick: handleRemoveAllItems,
              minWidth: 70,
              maxWidth: 70,
              className: styles.removeIconColumn,
              onRender: (item, index) => {
                const iconName = 'Cancel';
                return (
                  <IconButton
                    iconProps={{ iconName }}
                    ariaLabel="remove"
                    menuIconProps={{ iconName: '' }}
                    id={`itemRemoveButton${index}`}
                    onClick={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleRemoveItem(item, index);
                    }}
                  />
                );
              },
            },
          ];

          if (currentListType === 'product') stagingColumns.splice(2, 0, viewStagingColumn);
          if (dLTypeValues[currentListType].hierarchy)
            stagingColumnsItems.splice(1, 0, hierarchyColumn);

          return (
            <>
              <Form>
                <Module>
                  <ModuleContent>
                    <SubModule disableAccordian>
                      <SubModuleHeader variant="medium">List Overview</SubModuleHeader>
                      <SubModuleContent>
                        <StackGrid smCols={1} mdCols={2} lgCols={2} shrink>
                          <Field
                            name="listType"
                            label="List Type"
                            options={dimensionListTypes}
                            component={Dropdown}
                            disabled={reviewMode || isSubmitting || disableListType}
                            data-testid="dimensionListFormListType"
                            required
                          />
                          {showSiloField && (
                            <Field
                              name="metadata.silo"
                              label="Silo"
                              options={dlSilos}
                              component={Dropdown}
                              disabled={reviewMode || isSubmitting}
                              validate={value => {
                                if (!value) return 'Required';
                                return '';
                              }}
                              data-testid="dimensionListFormSilo"
                              required
                            />
                          )}
                          <Field
                            name="listName"
                            label="List Name"
                            component={TextField}
                            placeholder="Enter a name"
                            maxLength={255}
                            description={listNameCharLimit}
                            data-testid="dimensionListFormListName"
                            required
                            disabled={reviewMode || isSubmitting}
                          />
                          <Field
                            name="listDescription"
                            label="Description"
                            component={TextField}
                            placeholder="Enter a description"
                            data-testid="dimensionListFormListDescription"
                            disabled={reviewMode || isSubmitting}
                          />
                        </StackGrid>
                      </SubModuleContent>
                    </SubModule>
                    {listId && associatedContractDetails && (
                      <SubModule>
                        <SubModuleHeader id="associatedContractDetails" variant="medium">
                          {' '}
                          Associated Contracts{' '}
                        </SubModuleHeader>
                        <SubModuleContent>
                          {associatedContractDetails.length > 0 && (
                            <DimensionListItems
                              shimmered
                              items={associatedContractDetails}
                              isAssociatedContracts
                              className={styles.scrollablePane}
                            />
                          )}
                          {associatedContractDetails.length === 0 && (
                            <MessageBar isMultiline className={styles.noDimensionDetail}>
                              <Text>No Associated Contracts</Text>
                              <br />
                              <Text className={styles.associatedContracts}>
                                Associate a contract from the contract detail page
                              </Text>
                            </MessageBar>
                          )}
                        </SubModuleContent>
                      </SubModule>
                    )}
                    <SubModule>
                      <SubModuleHeader id={`${currentListType}ListItems`} variant="medium">
                        <span>
                          {dimensionListTypes.find(e => e.key === currentListType).text} List Items
                        </span>
                      </SubModuleHeader>
                      <SubModuleContent>
                        {errors.products && (
                          <MessageBar
                            messageBarType={MessageBarType.error}
                            messageBarIconProps={{ iconName: 'Info' }}
                            isMultiline
                            dismissButtonAriaLabel="Close"
                            nowrap
                          >
                            {errors.products}
                          </MessageBar>
                        )}
                        <Field name="listItems" data-testid="dimensionListFormListItems" required>
                          {({ field: { value } }) => (
                            <>
                              {!isEmpty(value) && (
                                <DimensionSearchList
                                  columns={stagingColumns}
                                  items={value.filter(item => item.isList)}
                                  disableRowLink
                                  shimmered
                                  enableView
                                  noBorderBottom
                                  className={styles.table}
                                />
                              )}
                              {!isEmpty(value) && (
                                <DimensionSearchList
                                  columns={stagingColumnsItems}
                                  items={value.filter(item => !item.isList)}
                                  disableRowLink
                                  shimmered
                                  enableView
                                  noBorderBottom
                                  className={styles.table}
                                />
                              )}
                            </>
                          )}
                        </Field>
                        {enableListItemTextField && (
                          <div className={styles.root}>
                            <StackGrid smCols={3} mdCols={3} lgCols={3} shrink>
                              <FluentTextField
                                name="newListItem"
                                value={newDimensionListItem}
                                onChange={(e, val) => {
                                  setNewDimensionListItem(val);
                                }}
                                label={dimensionListTypes.find(e => e.key === currentListType).text}
                                component={TextField}
                                required
                              />
                              <IconButton
                                iconProps={{ iconName: 'Cancel' }}
                                title="Close"
                                ariaLabel="Close"
                                className={styles.toggleCloseButton}
                                onClick={() => {
                                  setNewDimensionListItem(null);
                                  setEnableListItemTextField(false);
                                }}
                              />
                              <TextButton
                                type="button"
                                data-testid="save-new-list-item-button"
                                onClick={handleNewListItemSave}
                                className={styles.saveDimensionListItemButton}
                              >
                                Save {dimensionListTypes.find(e => e.key === currentListType).text}
                              </TextButton>
                            </StackGrid>
                          </div>
                        )}
                        {!dLTypeValues[currentListType]?.isTextField && (
                          <Link
                            className={styles.productAddButton}
                            to={{
                              pathname: `/dimensionlists/add/search/${currentListType}`,
                              listFormData: values,
                            }}
                            data-testid="dimensionListFormAddItemsLink"
                          >
                            Add {dimensionListTypes.find(e => e.key === currentListType).text} or{' '}
                            {''}
                            {dimensionListTypes.find(e => e.key === currentListType).text} List
                          </Link>
                        )}
                        {dLTypeValues[currentListType]?.isTextField && (
                          <TextButton
                            name="newListItemId"
                            type="button"
                            disabled={enableListItemTextField}
                            onClick={() => {
                              setEnableListItemTextField(true);
                            }}
                            data-testid="add-listItemId"
                          >
                            Add {dimensionListTypes.find(e => e.key === currentListType).text} or{' '}
                            {''}
                            {dimensionListTypes.find(e => e.key === currentListType).text} List
                          </TextButton>
                        )}
                      </SubModuleContent>
                    </SubModule>
                  </ModuleContent>
                </Module>

                {listId && associatedContractDetails && associatedContractDetails.length > 0 && (
                  <Module>
                    <ModuleContent>
                      <SubModule disableAccordian>
                        <SubModuleHeader variant="large">Reviewer</SubModuleHeader>
                        <SubModuleContent>
                          <StackGrid smCols={1} mdCols={2} lgCols={2} shrink>
                            <Field
                              name="reviewer.reviewerId"
                              label="Reviewer"
                              component={Dropdown}
                              options={reviewers}
                              disabled={isSubmitting}
                              required
                              data-testid="adjustmentFormReviewerName"
                            />

                            <Field
                              name="reviewer.reviewerJustification"
                              label="Reviewer Justification"
                              component={TextField}
                              disabled={reviewMode || isSubmitting}
                              multiline
                              required
                              data-testid="adjustmentFormReviewerJustification"
                            />
                          </StackGrid>
                        </SubModuleContent>
                      </SubModule>
                    </ModuleContent>
                  </Module>
                )}
                <div className={styles.buttonContainer}>
                  <TextButton
                    className={styles.cancelButton}
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </TextButton>
                  {isResubmitting && (
                    <DefaultButton onClick={handleDiscard} disabled={isSubmitting}>
                      Discard Edit
                    </DefaultButton>
                  )}
                  {!isSubmitting ? (
                    <PrimaryButton
                      type="button"
                      onClick={handleSubmit}
                      disabled={!dirty || isSubmitting}
                      data-testid="dimensionListFormSave"
                    >
                      {submitBtnText()}
                    </PrimaryButton>
                  ) : (
                    <SavingIndicator />
                  )}
                </div>
              </Form>
            </>
          );
        }}
      </Formik>
    );
  },
);

DimensionListForm.propTypes = {
  onSubmit: PropTypes.func,
  onSuccess: PropTypes.func,
  onFailure: PropTypes.func,
  reviewMode: PropTypes.bool,
  dimensionList: PropTypes.object,
  handleCancel: PropTypes.func,
  handleSubmit: PropTypes.func,
  handleDiscard: PropTypes.func,
};

DimensionListForm.defaultProps = {
  onSubmit: null,
  onSuccess: () => {},
  onFailure: () => {},
  /**
   * When true, the form fields will all be disabled and nothing will be editable.
   */
  reviewMode: false,
  dimensionList: null,
  handleCancel: () => {},
  handleSubmit: () => {},
  handleDiscard: () => {},
};

export default DimensionListForm;
